/**
 * DevExtreme (esm/__internal/ui/calendar/calendar.range.selection.strategy.js)
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 * Read about DevExtreme licensing here: https://js.devexpress.com/Licensing/
 */
import dateUtils from "../../../core/utils/date";
import CalendarSelectionStrategy from "./calendar.selection.strategy";
const DAY_INTERVAL = 864e5;
class CalendarRangeSelectionStrategy extends CalendarSelectionStrategy {
  constructor(component) {
    super(component);
    this.NAME = "RangeSelection";
  }
  dateOption(optionName) {
    if ("value" === optionName) {
      return this.calendar._getDateOption("value") || null;
    }
    return this.calendar._getDateOption(optionName);
  }
  getViewOptions() {
    const value = this._getValue();
    const range = this._getDaysInRange(value[0], value[1]);
    return {
      value: value,
      range: range,
      selectionMode: "range",
      onCellHover: this._cellHoverHandler.bind(this),
      onWeekNumberClick: this._shouldHandleWeekNumberClick() ? this._weekNumberClickHandler.bind(this) : null
    };
  }
  selectValue(selectedValue, e) {
    const [startDate, endDate] = this._getValue();
    this.skipNavigate();
    this._updateCurrentDate(selectedValue);
    this._currentDateChanged = true;
    const {
      allowChangeSelectionOrder: allowChangeSelectionOrder,
      currentSelection: currentSelection
    } = this.calendar.option();
    if (true === allowChangeSelectionOrder) {
      this.calendar._valueSelected = true;
      const convertedSelectedValue = this.calendar._convertToDate(selectedValue);
      if ("startDate" === currentSelection) {
        if (convertedSelectedValue > (this.calendar._convertToDate(endDate) ?? new Date(0))) {
          this.dateValue([selectedValue, null], e);
        } else {
          this.dateValue([selectedValue, endDate], e);
        }
      } else if (convertedSelectedValue >= (this.calendar._convertToDate(startDate) ?? new Date(0))) {
        this.dateValue([startDate, selectedValue], e);
      } else {
        this.dateValue([selectedValue, null], e);
      }
    } else if (!startDate || endDate) {
      this.dateValue([selectedValue, null], e);
    } else {
      this.dateValue(startDate < selectedValue ? [startDate, selectedValue] : [selectedValue, startDate], e);
    }
  }
  updateAriaSelected(val, previousVal) {
    const value = val ?? this._getValue();
    const previousValue = previousVal ?? [];
    super.updateAriaSelected(value, previousValue);
  }
  processValueChanged(value, previousValue) {
    super.processValueChanged(value, previousValue);
    const range = this._getRange();
    this._updateViewsOption("range", range);
  }
  getDefaultCurrentDate() {
    const {
      allowChangeSelectionOrder: allowChangeSelectionOrder,
      currentSelection: currentSelection
    } = this.calendar.option();
    const value = this.dateOption("value");
    if (allowChangeSelectionOrder) {
      if ("startDate" === currentSelection && value[0]) {
        return value[0];
      }
      if ("endDate" === currentSelection && value[1]) {
        return value[1];
      }
    }
    const dates = value.filter(date => null !== date);
    return this._getLowestDateInArray(dates);
  }
  restoreValue() {
    this.calendar.option("value", [null, null]);
  }
  _getValue() {
    const value = this.dateOption("value");
    if (!value.length) {
      return value;
    }
    let [startDate, endDate] = value;
    if (startDate && endDate && startDate > endDate) {
      [startDate, endDate] = [endDate, startDate];
    }
    return [startDate, endDate];
  }
  _getRange() {
    const [startDate, endDate] = this._getValue();
    return this._getDaysInRange(startDate, endDate);
  }
  _getDaysInRange(startDate, endDate) {
    if (!startDate || !endDate) {
      return [];
    }
    const {
      currentDate: currentDate,
      viewsCount: viewsCount
    } = this.calendar.option();
    const isAdditionalViewDate = this.calendar._isAdditionalViewDate(currentDate);
    const firstDateInViews = dateUtils.getFirstMonthDate(currentDate, isAdditionalViewDate ? -2 : -1);
    const lastDateInViews = dateUtils.getLastMonthDate(currentDate, isAdditionalViewDate ? 1 : viewsCount);
    const rangeStartDate = new Date(Math.max(firstDateInViews.getTime(), startDate.getTime()));
    const rangeEndDate = new Date(Math.min(lastDateInViews.getTime(), endDate.getTime()));
    return [...dateUtils.getDatesOfInterval(rangeStartDate, rangeEndDate, 864e5), rangeEndDate];
  }
  _cellHoverHandler(e) {
    const isMaxZoomLevel = this._isMaxZoomLevel();
    const [startDate, endDate] = this._getValue();
    const {
      allowChangeSelectionOrder: allowChangeSelectionOrder,
      currentSelection: currentSelection
    } = this.calendar.option();
    if (isMaxZoomLevel) {
      const skipHoveredRange = allowChangeSelectionOrder && "startDate" === currentSelection;
      if (startDate && !endDate && !skipHoveredRange) {
        if (e.value > startDate) {
          this._updateViewsOption("hoveredRange", this._getDaysInRange(startDate, e.value));
          return;
        }
      } else if (!startDate && endDate && !(allowChangeSelectionOrder && "endDate" === currentSelection)) {
        if (e.value < endDate) {
          this._updateViewsOption("hoveredRange", this._getDaysInRange(e.value, endDate));
          return;
        }
      } else if (startDate && endDate) {
        if ("startDate" === currentSelection && e.value < startDate) {
          this._updateViewsOption("hoveredRange", this._getDaysInRange(e.value, startDate));
          return;
        }
        if ("endDate" === currentSelection && e.value > endDate) {
          this._updateViewsOption("hoveredRange", this._getDaysInRange(endDate, e.value));
          return;
        }
      }
      this._updateViewsOption("hoveredRange", []);
    }
  }
  _weekNumberClickHandler(_ref) {
    let {
      rowDates: rowDates,
      event: event
    } = _ref;
    const selectedDates = rowDates.filter(date => !this._isDateDisabled(date));
    const value = selectedDates.length ? [selectedDates[0], selectedDates[selectedDates.length - 1]] : [null, null];
    this.dateValue(value, event);
  }
}
export default CalendarRangeSelectionStrategy;